import { DateFormats, formatDate, useDebouncedFunction } from '@strata/helper-functions';
import { ChatWithConversationPane, SeenIndicator } from '@strata/react-chat';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    ColumnDef,
    FilterCategory,
    GridWithFilters,
    PrimaryData,
    SecondaryData,
} from '@strata/react-grid';
import { StatusIndicator } from '@strata/react-status-indicators';
import classNames from 'classnames';
import { ComponentProps, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppRoutes } from 'services/routes';
import { MappedConversation, MESSAGES_LOCATION_HASH } from 'services/userSupportHelpers';
import { useAppDispatch, useAppSelector } from 'store';
import { useLoggedInSupplierName } from 'store/auth/authThunks';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridThunks';
import { resetPageData } from 'store/userSupport/userSupportSlice';
import {
    fetchConversationFilterCategories,
    fetchConversations,
    updatePage,
} from 'store/userSupport/userSupportThunks';

const initialSortProperties: ComponentProps<
    typeof GridWithFilters<MappedConversation>
>['sortProperties'] = [{ id: 'lastUpdated', desc: true }];

const columns: ColumnDef<MappedConversation>[] = [
    {
        accessorKey: 'isUnread',
        header: '',
        size: 1,
        cell: ({ getValue }) => (
            <SeenIndicator seen={!getValue()} size={SeenIndicator.sizes.LARGE} />
        ),
    },
    {
        accessorKey: 'subject',
        header: 'Subject',
        size: 180,
        cell: ({ getValue }) => (
            <div
                className={classNames({
                    'sds_messages--unread': getValue()?.isUnread,
                })}
            >
                <PrimaryData data={getValue()?.title} />
            </div>
        ),
    },
    {
        accessorKey: 'lastMessage',
        header: 'Last Message',
        size: 180,
    },
    {
        accessorKey: 'memberFullName',
        header: 'Patient',
        size: 100,
    },
    {
        accessorKey: 'createdBy',
        header: 'Created By',
        size: 70,
    },
    {
        accessorKey: 'lastUpdated',
        header: 'Last Updated',
        size: 100,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.TIME_DATE_SHORT)} />
        ),
    },
    {
        accessorKey: 'conversationStatus',
        header: 'Conversation Status',
        size: 80,
        cell: ({ getValue }) => (
            <StatusIndicator
                message={getValue()}
                status={
                    (
                        {
                            [ChatWithConversationPane.ConversationStatus.OPEN]:
                                StatusIndicator.Status.SUCCESS,
                            [ChatWithConversationPane.ConversationStatus.CLOSED]:
                                StatusIndicator.Status.DEFAULT,
                        } as Record<
                            (typeof ChatWithConversationPane.ConversationStatus)[keyof typeof ChatWithConversationPane.ConversationStatus],
                            (typeof StatusIndicator.Status)[keyof typeof StatusIndicator.Status]
                        >
                    )[getValue()]
                }
            />
        ),
    },
];

export const AllMessages = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const loggedInSupplierName = useLoggedInSupplierName();
    const debouncedSearchOrFilter = useDebouncedFunction();
    const [fetchedOnce, setFetchedOnce] = useState(false);

    const { currentPage, pages, conversationCount } = useAppSelector(
        (state) => state.userSupport.pageData,
    );

    const { conversationFilterCategories } = useAppSelector((state) => state.userSupport);

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
    } = useGridState(Grids.CONVERSATIONS, initialSortProperties);

    const [searching, setSearching] = useState(false);

    useEffect(() => {
        dispatch(fetchConversationFilterCategories());
    }, [dispatch]);

    useEffect(() => {
        if (!fetchedOnce || !pages[currentPage]) {
            dispatch(fetchConversations(search));
        }
        setFetchedOnce(true);
    }, [currentPage, dispatch, fetchedOnce, pages, search]);

    return (
        <FormPageContainer>
            <GridWithFilters
                title="All Messages"
                secondaryTitle={loggedInSupplierName}
                data={pages[currentPage]}
                isLoading={!pages[currentPage] || searching}
                enableSorting={false}
                sortProperties={sort}
                enableSearch
                searchText={search}
                onSearch={(searchText) => {
                    setSearching(true);
                    updateGridSearch?.(searchText);
                    debouncedSearchOrFilter(() => {
                        dispatch(resetPageData());
                        setSearching(false);
                    });
                }}
                filterCategories={
                    // Since grid is filtered on backend, data & filters don't have to match
                    conversationFilterCategories as FilterCategory<MappedConversation>[]
                }
                activeFilters={activeFilters}
                onFilter={(filters) => {
                    setSearching(true);
                    updateGridActiveFilters?.(filters);
                    debouncedSearchOrFilter(() => {
                        dispatch(resetPageData());
                        setSearching(false);
                    });
                }}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                currentPage={currentPage}
                totalCount={conversationCount}
                onPageChange={(page) => dispatch(updatePage(page))}
                onRowClick={(rowData) => {
                    navigate(
                        {
                            pathname: AppRoutes.wonReferralDetails(rowData.referralId),
                            hash: MESSAGES_LOCATION_HASH,
                        },
                        {
                            state: {
                                conversationId: rowData.conversationId,
                                backToDestination: {
                                    text: 'Back to All Messages',
                                    route: AppRoutes.allMessages,
                                },
                            },
                        },
                    );
                }}
                columns={columns}
            />
        </FormPageContainer>
    );
};
